import { z } from 'zod';
import { SkuDivision } from './master.js';
import { PlanogramGrade, PlanogramRank } from './planogram.js';
import { Id, Timestamp } from './primitives.js';
import { PhotoAnalysis } from './vision.js';
/**
 * Server-Sent Events envelope — step 9 of the TAD 4.3 pipeline.
 *
 * The client falls back to 1.5-second polling when the SSE channel is
 * unavailable, so every event type here must also be expressible as a GET on
 * the visit. Nothing may exist only as a push.
 */
export const SseEventType = z.enum([
    'PHOTO_PROCESSING', //  worker picked the job up
    'PHOTO_ANALYSED', //    result available (FR-05)
    'PHOTO_FAILED', //      terminal failure after retries and fallback (TSP 3.5)
    'HEARTBEAT', //         keeps intermediaries from closing the stream
]);
export const SseEvent = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('PHOTO_PROCESSING'),
        photoId: Id,
        visitId: Id,
        at: Timestamp,
    }),
    z.object({
        type: z.literal('PHOTO_ANALYSED'),
        photoId: Id,
        visitId: Id,
        analysis: PhotoAnalysis,
        at: Timestamp,
    }),
    z.object({
        type: z.literal('PHOTO_FAILED'),
        photoId: Id,
        visitId: Id,
        /** Shown to the user; must be honest about what happened (TSP 3.5). */
        message: z.string(),
        retryable: z.boolean(),
        at: Timestamp,
    }),
    z.object({ type: z.literal('HEARTBEAT'), at: Timestamp }),
]);
/** Polling fallback response — the same information, pulled. */
export const VisitPhotoStatusResponse = z.object({
    visitId: Id,
    /**
     * Photos registered on the server that no submit has covered yet — the number
     * the FR-08 check-out gate is built on.
     *
     * IT CANNOT BE DERIVED ON THE CLIENT, which is the reason it travels here.
     * `ADD_PHOTO` is a self-loop on SUBMITTED, so a visit that was submitted and
     * then had another photo analysed still reads SUBMITTED — the state says
     * nothing about whether the latest photograph was sent. Only the server knows
     * how the photo timestamps sit against the last submission.
     *
     * Zero is the ordinary answer: everything sent, or nothing taken.
     */
    unsubmittedPhotos: z.number().int().nonnegative(),
    photos: z.array(z.object({
        photoId: Id,
        processing: z.enum(['QUEUED', 'RUNNING', 'DONE', 'FAILED']),
        analysis: PhotoAnalysis.nullable(),
        /**
         * Whether a submit has covered THIS photograph — the per-row answer the
         * count above cannot give.
         *
         * IT IS WHAT DECIDES IF THE CARD OFFERS "HAPUS FOTO". A photograph nobody
         * has sent is in no report and may still be withdrawn and retaken; once
         * sent it is evidence and the server refuses to delete it. The client
         * needs to know which of the two it is holding BEFORE the user taps,
         * because a disabled control that says why beats a refusal afterwards.
         *
         * Same comparison as `unsubmittedPhotos`, per row — see `isPhotoSent`.
         */
        submitted: z.boolean(),
    })),
    /**
     * The two FR-05 verdicts for this visit — HSJ and LKJ.
     *
     * THE NUMBER THE SALESPERSON IS ACTUALLY MEASURED ON, and until v4 the app
     * could not show it: the score was per photograph, and a visit total was
     * something only the console computed. It rides on the poll the app already
     * makes rather than a second endpoint, because it changes for exactly the same
     * reasons the photo scores do — a new photograph, or a correction.
     *
     * EMPTY UNTIL SOMETHING IS SCORED. A visit whose photographs are all still
     * queued has no verdict yet, which is different from a verdict of zero.
     */
    divisionScores: z.array(z.object({
        division: SkuDivision,
        totalScore: z.number().min(0).max(100),
        rank: PlanogramRank,
        /** Distinct internal SKUs of this division found anywhere in the visit. */
        availabilityCount: z.number().int().nonnegative(),
        availabilityGrade: PlanogramGrade,
    })),
});
//# sourceMappingURL=events.js.map